import { createContext, useContext, useState, type ReactNode } from 'react';
import { useAuth } from './useAuth';
import { useRessource } from './useRessource';
import { api } from '../lib/apiClient';
import type { AnneeScolaire } from '../types';

/**
 * L'ANNEE DE TRAVAIL, partagee par toute l'application.
 *
 * Elle vivait dans chaque ecran (`useAnneeSelectionnee`), avec son propre
 * etat et son propre selecteur : passer du planning aux inscriptions
 * ramenait a l'annee active, et neuf ecrans portaient neuf listes
 * deroulantes. C'est desormais un contexte, choisi une fois dans le
 * bandeau — au meme titre que l'entite active.
 *
 * DEUX DIMENSIONS DE CONTEXTE, une seule regle : ce que dit le bandeau
 * est ce que montrent les ecrans. Un ecran qui refuserait l'annee
 * choisie doit le DIRE et passer en lecture seule, jamais en afficher
 * une autre en silence.
 *
 * L'ANNEE EST COMMUNE a toutes les ecoles (2026-08-29) : changer d'ecole
 * ne change plus la liste, et le choix survit tel quel. Le repli par
 * LIBELLE qui reportait 2025-26 d'une ecole a l'autre a disparu avec le
 * cloisonnement qui le rendait necessaire.
 */

export interface AnneeDeTravail {
  annees: AnneeScolaire[];
  anneeId: string;
  annee: AnneeScolaire | null;
  choisir: (id: string) => void;
  chargement: boolean;
  erreur: string | null;
  recharger: () => void;
  /** RG-27 : une annee clouturee est figee, l'ecran cesse de proposer d'y ecrire. */
  modifiable: boolean;
}

const Contexte = createContext<AnneeDeTravail | null>(null);

export function FournisseurAnnee({ children }: { children: ReactNode }) {
  // La liste ne depend plus de l'entite ; elle est rechargee au
  // changement d'ecole malgre tout, parce que la session change.
  const { entiteActive } = useAuth();
  const cle = entiteActive?.id ?? '';

  const { donnees, chargement, erreur, recharger } = useRessource(
    () => api.get<AnneeScolaire[]>('/api/annees'),
    [cle],
  );

  const [choisie, setChoisie] = useState('');

  // Triees par date de debut decroissante cote serveur.
  const annees = donnees ?? [];

  // A defaut d'annee active, la plus recente : une entite sans annee
  // ouverte doit rester consultable.
  const defaut = annees.find((a) => a.statut === 'active')?.id ?? annees[0]?.id ?? '';

  const anneeId = annees.some((a) => a.id === choisie) ? choisie : defaut;
  const annee = annees.find((a) => a.id === anneeId) ?? null;

  const choisir = (id: string) => setChoisie(id);

  return (
    <Contexte.Provider
      value={{
        annees,
        anneeId,
        annee,
        choisir,
        chargement,
        erreur,
        recharger,
        modifiable: annee !== null && annee.statut !== 'cloturee',
      }}
    >
      {children}
    </Contexte.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAnnee(): AnneeDeTravail {
  const contexte = useContext(Contexte);
  if (!contexte) {
    throw new Error('useAnnee doit etre appele sous <FournisseurAnnee>.');
  }
  return contexte;
}
